import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { countryInfosQuery } from 'api/queries';
import { mapCountryInfoToIdLabelCountry } from 'api/responses/models/Configuration/CountryInfoDto';
import { isUSTerritory } from 'library/formatters/LocationFormatter';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';

const excludedCountries: string =
	import.meta.env.VITE_EXCLUDED_COUNTRIES_CODES?.split(',') || [];

export const useProductCenterCountries = () => {
	const languageCode = useAppSelector(selectUser).language;
	const { api } = useProductCenterApi();
	const { t } = useTranslation(['common', 'error']);
	const { data } = useQuery(countryInfosQuery(api, languageCode));

	const countryOptions = useMemo(
		() =>
			data
				?.filter(
					({ alpha2 }) =>
						alpha2 !== null && !excludedCountries.includes(alpha2)
				)
				.map(mapCountryInfoToIdLabelCountry),
		[data, excludedCountries]
	);

	const countryOptionsWithUS = useMemo(() => {
		const getIsTerritoryText = (id: string) =>
			isUSTerritory(id) ? ` (${t('common:us-territory')})` : '';

		return countryOptions?.map(({ id, label }) => ({
			id,
			label: `${label}${getIsTerritoryText(id)}`,
		}));
	}, [countryOptions]);

	return { countryOptions, countryOptionsWithUS, excludedCountries };
};
